import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Flex, Loader, Text } from '@vibe/core';
import { getTimesheetTimeline, type TimesheetAggRow } from '../../api/timesheet.api';
import { getWorklogCategories, type WorklogCategory } from '../../api/worklog-category.api';
import { useTimesheetStore } from '../../stores/timesheetStore';
import { useUsersStore } from '../../stores/usersStore';
import { fetchBoardsByIds } from '../../api/monday.api';
import { formatHours } from '../../utils/timesheet-capacity';
import {
  createWorkbook, addTitleRow, styleHeaderRow, styleGroupRow, styleDataRow,
  styleTotalRow, setColumnWidths, downloadWorkbook, hexToArgb, round, COLORS,
} from '../../utils/export-report-excel';

// ─── Types ──────────────────────────────────────────────────────────────────

type GroupDimension = 'user' | 'board' | 'category';

interface ReportRow {
  key: string;
  label: string;
  color?: string;
  hours: number;
  billableHours: number;
  worklogCount: number;
  children: ReportRow[];
  depth: number;
}

// ─── Grouping options ───────────────────────────────────────────────────────

const DIMENSION_OPTIONS: { value: GroupDimension; label: string }[] = [
  { value: 'user', label: 'User' },
  { value: 'board', label: 'Board' },
  { value: 'category', label: 'Category' },
];

// ─── Component ──────────────────────────────────────────────────────────────

interface TimesheetReportViewProps {
  onExportReady?: (exportFn: (() => void) | null) => void;
}

const TimesheetReportView: React.FC<TimesheetReportViewProps> = ({ onExportReady }) => {
  const dateRange = useTimesheetStore((s) => s.dateRange);
  const filters = useTimesheetStore((s) => s.filters);
  const users = useUsersStore((s) => s.users);

  const [primaryGroup, setPrimaryGroup] = useState<GroupDimension>('user');
  const [secondaryGroup, setSecondaryGroup] = useState<GroupDimension>('category');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<TimesheetAggRow[]>([]);
  const [categories, setCategories] = useState<WorklogCategory[]>([]);
  const [boardNames, setBoardNames] = useState<Map<string, string>>(new Map());
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  // Fetch data
  const hasSelection = filters.userIds.length > 0;

  const fetchData = useCallback(async () => {
    if (!dateRange.start || !dateRange.end) return;

    // Require user/team selection before loading
    if (filters.userIds.length === 0) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Use groupBy='board' to get user→board hierarchy, plus subGroupBy='category'
      // This gives us all 3 dimensions: groupKey=userId, childKey=boardId, categoryId
      const [timelineResp, cats] = await Promise.all([
        getTimesheetTimeline({
          startDate: dateRange.start,
          endDate: dateRange.end,
          groupBy: 'board',
          userIds: filters.userIds,
          boardIds: filters.boardIds.length > 0 ? filters.boardIds : undefined,
          subGroupBy: 'category',
        }),
        getWorklogCategories(false),
      ]);

      setRows(timelineResp.rows);
      setCategories(cats);

      // Resolve board names
      const boardIds = [...new Set(timelineResp.rows.map((r) => r.childKey).filter(Boolean))] as string[];
      if (boardIds.length > 0) {
        try {
          const boards = await fetchBoardsByIds(boardIds);
          const map = new Map<string, string>();
          for (const b of boards) map.set(String(b.id), b.name);
          setBoardNames(map);
        } catch { /* ignore */ }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [dateRange, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Resolve labels
  const resolveUser = useCallback((id: string) => {
    const u = users.find((u) => String(u.id) === id);
    return u ? u.name : `User #${id}`;
  }, [users]);

  const resolveBoard = useCallback((id: string) => {
    return boardNames.get(id) ?? `Board #${id}`;
  }, [boardNames]);

  const resolveCategory = useCallback((id: string | null | undefined) => {
    if (!id || id === '__uncategorized__') return 'Uncategorized';
    const cat = categories.find((c) => c.id === id);
    return cat?.name ?? 'Uncategorized';
  }, [categories]);

  const getCategoryColor = useCallback((id: string | null | undefined) => {
    if (!id) return '#808080';
    const cat = categories.find((c) => c.id === id);
    return cat?.color ?? '#808080';
  }, [categories]);

  // Build report tree from flat rows
  const reportTree = useMemo((): ReportRow[] => {
    // Backend returns: groupKey=userId, childKey=boardId, categoryId from subGroupBy
    const getDimensionKey = (row: TimesheetAggRow, dim: GroupDimension): string => {
      switch (dim) {
        case 'user': return row.groupKey;
        case 'board': return row.childKey ?? '__no_board__';
        case 'category': return row.categoryId ?? '__uncategorized__';
      }
    };

    const getPrimaryKey = (row: TimesheetAggRow): string => getDimensionKey(row, primaryGroup);
    const getSecondaryKey = (row: TimesheetAggRow): string => getDimensionKey(row, secondaryGroup);

    const resolveLabel = (dim: GroupDimension, key: string): string => {
      switch (dim) {
        case 'user': return resolveUser(key);
        case 'board': return resolveBoard(key);
        case 'category': return resolveCategory(key);
      }
    };

    const getColor = (dim: GroupDimension, key: string): string | undefined => {
      if (dim === 'category') return getCategoryColor(key);
      return undefined;
    };

    // Group: primary → secondary → aggregate hours
    const primaryMap = new Map<string, Map<string, { hours: number; billableHours: number; worklogCount: number }>>();

    for (const row of rows) {
      const pk = getPrimaryKey(row);
      const sk = getSecondaryKey(row);

      let secondaryMap = primaryMap.get(pk);
      if (!secondaryMap) {
        secondaryMap = new Map();
        primaryMap.set(pk, secondaryMap);
      }

      const existing = secondaryMap.get(sk);
      if (existing) {
        existing.hours += row.hours;
        existing.billableHours += row.billableHours ?? 0;
        existing.worklogCount += row.worklogCount;
      } else {
        secondaryMap.set(sk, {
          hours: row.hours,
          billableHours: row.billableHours ?? 0,
          worklogCount: row.worklogCount,
        });
      }
    }

    // Build tree
    const tree: ReportRow[] = [];
    for (const [pk, secondaryMap] of primaryMap) {
      let totalHours = 0;
      let totalBillable = 0;
      let totalCount = 0;

      const children: ReportRow[] = [];

      if (primaryGroup !== secondaryGroup) {
        for (const [sk, agg] of secondaryMap) {
          totalHours += agg.hours;
          totalBillable += agg.billableHours;
          totalCount += agg.worklogCount;
          children.push({
            key: `${pk}::${sk}`,
            label: resolveLabel(secondaryGroup, sk),
            color: getColor(secondaryGroup, sk),
            hours: Math.round(agg.hours * 100) / 100,
            billableHours: Math.round(agg.billableHours * 100) / 100,
            worklogCount: agg.worklogCount,
            children: [],
            depth: 1,
          });
        }
      } else {
        // Same dimension: no children
        for (const [, agg] of secondaryMap) {
          totalHours += agg.hours;
          totalBillable += agg.billableHours;
          totalCount += agg.worklogCount;
        }
      }

      // Sort children by hours descending
      children.sort((a, b) => b.hours - a.hours);

      tree.push({
        key: pk,
        label: resolveLabel(primaryGroup, pk),
        color: getColor(primaryGroup, pk),
        hours: Math.round(totalHours * 100) / 100,
        billableHours: Math.round(totalBillable * 100) / 100,
        worklogCount: totalCount,
        children,
        depth: 0,
      });
    }

    // Sort primary rows by hours descending
    tree.sort((a, b) => b.hours - a.hours);
    return tree;
  }, [rows, primaryGroup, secondaryGroup, resolveUser, resolveBoard, resolveCategory, getCategoryColor]);

  // Grand total
  const grandTotal = useMemo(() => {
    return reportTree.reduce(
      (acc, r) => ({
        hours: acc.hours + r.hours,
        billableHours: acc.billableHours + r.billableHours,
        worklogCount: acc.worklogCount + r.worklogCount,
      }),
      { hours: 0, billableHours: 0, worklogCount: 0 },
    );
  }, [reportTree]);

  const toggleExpand = useCallback((key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedKeys(new Set(reportTree.map((r) => r.key)));
  }, [reportTree]);

  const handleExport = useCallback(async () => {
    if (reportTree.length === 0) return;

    const wb = createWorkbook();
    const ws = wb.addWorksheet('Logged Time Report');

    const startDate = dateRange.start;
    const endDate = dateRange.end;
    const primaryLabel = DIMENSION_OPTIONS.find((o) => o.value === primaryGroup)?.label ?? primaryGroup;
    const secondaryLabel = DIMENSION_OPTIONS.find((o) => o.value === secondaryGroup)?.label ?? secondaryGroup;

    addTitleRow(ws, `Logged Time Report (${primaryLabel} → ${secondaryLabel})`, startDate, endDate);

    const headers = [primaryLabel, secondaryLabel, 'Hours', 'Billable Hours', 'Entries'];
    const headerRow = ws.addRow(headers);
    styleHeaderRow(ws, headerRow);
    setColumnWidths(ws, [30, 30, 12, 14, 10]);

    for (const row of reportTree) {
      // Parent row
      const parentExcelRow = ws.addRow([row.label, '', round(row.hours), round(row.billableHours), row.worklogCount]);
      styleGroupRow(parentExcelRow);
      if (row.billableHours > 0) {
        parentExcelRow.getCell(4).font = { ...parentExcelRow.getCell(4).font as Record<string, unknown>, color: { argb: hexToArgb(COLORS.balanced) } };
      }

      // Children
      for (const child of row.children) {
        const childExcelRow = ws.addRow(['', child.label, round(child.hours), round(child.billableHours), child.worklogCount]);
        styleDataRow(childExcelRow, true);
        if (child.billableHours > 0) {
          childExcelRow.getCell(4).font = { ...childExcelRow.getCell(4).font as Record<string, unknown>, color: { argb: hexToArgb(COLORS.balanced) } };
        }
      }
    }

    // Total row
    const totalRow = ws.addRow(['Total', '', round(grandTotal.hours), round(grandTotal.billableHours), grandTotal.worklogCount]);
    styleTotalRow(totalRow);

    await downloadWorkbook(wb, `logged-time-report_${startDate}_${endDate}.xlsx`);
  }, [reportTree, grandTotal, dateRange, primaryGroup, secondaryGroup]);

  // Expose export to parent via callback
  useEffect(() => {
    onExportReady?.(handleExport);
    return () => onExportReady?.(null);
  }, [handleExport, onExportReady]);

  const collapseAll = useCallback(() => {
    setExpandedKeys(new Set());
  }, []);

  // Available secondary options exclude the primary
  const secondaryOptions = DIMENSION_OPTIONS.filter((o) => o.value !== primaryGroup);

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="rp-ts-report">
      {/* Config bar */}
      <div className="rp-ts-report__config">
        <Flex gap={Flex.gaps.SMALL} align={Flex.align.CENTER}>
          <Text type={Text.types.TEXT2}>Group by</Text>
          <Dropdown
            size={Dropdown.sizes.SMALL}
            options={DIMENSION_OPTIONS}
            value={DIMENSION_OPTIONS.find((o) => o.value === primaryGroup)}
            onChange={(opt) => {
              if (opt) {
                const val = (opt as { value: GroupDimension }).value;
                setPrimaryGroup(val);
                if (val === secondaryGroup) {
                  const fallback = DIMENSION_OPTIONS.find((o) => o.value !== val);
                  if (fallback) setSecondaryGroup(fallback.value);
                }
              }
            }}
            insideOverflowContainer
            insideOverflowWithTransformContainer
            className="rp-ts-report__dropdown"
          />
          <Text type={Text.types.TEXT2}>then by</Text>
          <Dropdown
            size={Dropdown.sizes.SMALL}
            options={secondaryOptions}
            value={secondaryOptions.find((o) => o.value === secondaryGroup)}
            onChange={(opt) => {
              if (opt) setSecondaryGroup((opt as { value: GroupDimension }).value);
            }}
            insideOverflowContainer
            insideOverflowWithTransformContainer
            className="rp-ts-report__dropdown"
          />
        </Flex>
        <Flex gap={Flex.gaps.XS}>
          <Button size={Button.sizes.SMALL} kind={Button.kinds.TERTIARY} onClick={expandAll}>
            Expand All
          </Button>
          <Button size={Button.sizes.SMALL} kind={Button.kinds.TERTIARY} onClick={collapseAll}>
            Collapse All
          </Button>
        </Flex>
      </div>

      {/* Table */}
      {!hasSelection ? (
        <div className="rp-ts-timeline__empty">
          <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
            Select teams or users to view the report.
          </Text>
        </div>
      ) : loading ? (
        <div className="rp-ts-report__loading">
          <Loader size={Loader.sizes.MEDIUM} />
        </div>
      ) : error ? (
        <div className="rp-ts-report__error">
          <Text style={{ color: '#e2445c' }}>{error}</Text>
        </div>
      ) : (
        <div className="rp-ts-report__table-wrap">
          <table className="rp-ts-report__table">
            <thead>
              <tr>
                <th className="rp-ts-report__th rp-ts-report__th--name">Name</th>
                <th className="rp-ts-report__th rp-ts-report__th--num">Hours</th>
                <th className="rp-ts-report__th rp-ts-report__th--num">Billable</th>
                <th className="rp-ts-report__th rp-ts-report__th--num">Entries</th>
              </tr>
            </thead>
            <tbody>
              {reportTree.map((row) => {
                const isExpanded = expandedKeys.has(row.key);
                const hasChildren = row.children.length > 0;
                return (
                  <React.Fragment key={row.key}>
                    <tr
                      className="rp-ts-report__row rp-ts-report__row--parent"
                      onClick={hasChildren ? () => toggleExpand(row.key) : undefined}
                      style={{ cursor: hasChildren ? 'pointer' : 'default' }}
                    >
                      <td className="rp-ts-report__td rp-ts-report__td--name">
                        <span className="rp-ts-report__expand-icon">
                          {hasChildren ? (isExpanded ? '▾' : '▸') : ' '}
                        </span>
                        {row.color && (
                          <span
                            className="rp-ts-report__color-dot"
                            style={{ backgroundColor: row.color }}
                          />
                        )}
                        <span className="rp-ts-report__label">{row.label}</span>
                      </td>
                      <td className="rp-ts-report__td rp-ts-report__td--num rp-ts-report__td--bold">
                        {row.hours === 0 ? '0' : formatHours(row.hours)}h
                      </td>
                      <td className="rp-ts-report__td rp-ts-report__td--num rp-ts-report__td--billable">
                        {row.billableHours === 0 ? '0' : formatHours(row.billableHours)}h
                      </td>
                      <td className="rp-ts-report__td rp-ts-report__td--num">
                        {row.worklogCount}
                      </td>
                    </tr>
                    {isExpanded && row.children.map((child) => (
                      <tr key={child.key} className="rp-ts-report__row rp-ts-report__row--child">
                        <td className="rp-ts-report__td rp-ts-report__td--name rp-ts-report__td--indent">
                          {child.color && (
                            <span
                              className="rp-ts-report__color-dot"
                              style={{ backgroundColor: child.color }}
                            />
                          )}
                          <span className="rp-ts-report__label">{child.label}</span>
                        </td>
                        <td className="rp-ts-report__td rp-ts-report__td--num">
                          {child.hours === 0 ? '0' : formatHours(child.hours)}h
                        </td>
                        <td className="rp-ts-report__td rp-ts-report__td--num rp-ts-report__td--billable">
                          {child.billableHours === 0 ? '0' : formatHours(child.billableHours)}h
                        </td>
                        <td className="rp-ts-report__td rp-ts-report__td--num">
                          {child.worklogCount}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="rp-ts-report__row rp-ts-report__row--total">
                <td className="rp-ts-report__td rp-ts-report__td--name rp-ts-report__td--bold">
                  Total
                </td>
                <td className="rp-ts-report__td rp-ts-report__td--num rp-ts-report__td--bold">
                  {grandTotal.hours === 0 ? '0' : formatHours(grandTotal.hours)}h
                </td>
                <td className="rp-ts-report__td rp-ts-report__td--num rp-ts-report__td--billable rp-ts-report__td--bold">
                  {grandTotal.billableHours === 0 ? '0' : formatHours(grandTotal.billableHours)}h
                </td>
                <td className="rp-ts-report__td rp-ts-report__td--num rp-ts-report__td--bold">
                  {grandTotal.worklogCount}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default TimesheetReportView;
